import { Box, Typography } from "@mui/material"
import { Outlet, useLocation } from "react-router-dom"
import { requiredPermissionForPath } from "@/components/Layout/LeftSidebarMenu"
import { useAppSelector } from "@/lib/hooks"
import { User } from "@/redux/model"
import { isAdminPortal } from "@/utilities/domain"

const readStoredUser = (): User | null => {
  try {
    return JSON.parse(localStorage.getItem("user") ?? "null")
  } catch {
    return null
  }
}

/**
 * Shows a "no access" message instead of a page the signed-in user's roles do
 * not allow, for example when its URL is typed directly. The API enforces the
 * same permissions; this only stops an empty or half-working page rendering.
 */
const PermissionGate = () => {
  const { pathname } = useLocation()
  const stateUser = useAppSelector((state) => state.auth.user)
  // On a fresh page load the store is briefly empty; the copy saved at login
  // lets the check run before the page mounts and fires refused requests.
  const user = stateUser ?? readStoredUser()
  const required = isAdminPortal()
    ? undefined
    : requiredPermissionForPath(pathname)

  // Without a known user there is nothing to check against.
  if (!required || !user || (user.permissions ?? []).includes(required)) {
    return <Outlet />
  }

  return (
    <Box sx={{ py: { xs: "50px", md: "90px" } }} className="text-center">
      <i
        className="material-symbols-outlined"
        style={{ fontSize: 64, opacity: 0.5 }}
      >
        lock
      </i>
      <Typography
        variant="h4"
        sx={{ fontSize: "21px", fontWeight: 700, mt: "12px", mb: "10px" }}
      >
        You do not have access to this page
      </Typography>
      <Typography>
        Your role does not include this section. Use the menu to go to a page
        you can open, or ask an administrator for access.
      </Typography>
    </Box>
  )
}

export default PermissionGate
